import { Drawer, DrawerBody, DrawerContent, DrawerOverlay } from '@chakra-ui/react'
import { selectAuthenticatedUser, useSelector } from '@missionlabs/api'
import {
    DrawerHeader,
    DrawerProps,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs
} from '@missionlabs/react'
import { Destination, DestinationTypes } from '@missionlabs/types'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useGetUserData } from 'shared/hooks/useGetUserData'
import { Features } from 'shared/hooks/useHasClientFeature'
import { useHasFeature } from 'shared/hooks/useHasFeature'
import { useHasUserActiveESim } from 'shared/hooks/useHasUserActiveESim'
import { useIsClientActive } from 'shared/hooks/useIsClientActive'
import { usePostDestinationForUserMutation } from 'shared/store'

import { AddDeviceESimFields, AddDeviceESimForm } from './AddDeviceESimForm'
import { AddDeviceNumberFields, AddDeviceNumberForm } from './AddDeviceNumberForm'
import { AddDeviceSipEndpointFields, AddDeviceSipEndpointForm } from './AddDeviceSipEndpointForm'

const noop = () => {}

export interface AddDeviceDrawerProps extends Omit<DrawerProps, 'children'> {
    destinations: Destination[]
}

export const AddDeviceDrawer: FC<AddDeviceDrawerProps> = ({
    isOpen,
    onClose,
    destinations,
    ...props
}) => {
    const navigate = useNavigate()
    const { t } = useTranslation()

    const { user: userData } = useGetUserData()
    const user = useSelector(selectAuthenticatedUser)
    const isESimEnabled = useHasFeature(Features.esim)

    const isClientActive = useIsClientActive()
    const hasUserActiveESim = useHasUserActiveESim()
    const hasRedirect = destinations.some(
        destination => destination.type === DestinationTypes.number
    )

    const isESimTabVisible = [
        userData?.enabled,
        isESimEnabled,
        isClientActive,
        !hasUserActiveESim
    ].every(Boolean)

    const [createDestination, { data, isLoading }] = usePostDestinationForUserMutation()

    async function handleSubmit(
        data: AddDeviceSipEndpointFields | AddDeviceNumberFields | AddDeviceESimFields,
        closeOnSubmit = true
    ) {
        if (!user) return

        if (closeOnSubmit) {
            onClose()
        }

        await createDestination({
            userID: user.userID,
            payload: data as Partial<Destination>
        }).unwrap()
    }

    function handleTransition() {
        if (data?.type === DestinationTypes.sipEndpoint) {
            navigate(`/settings/devices/${data.ID}/edit`)
        } else {
            navigate('/settings/devices')
        }
    }

    return (
        <Drawer
            {...props}
            size="lg"
            isOpen={isOpen}
            onClose={!isLoading ? onClose : noop}
            onCloseComplete={handleTransition}
        >
            <DrawerOverlay />
            <DrawerContent>
                <DrawerHeader>{t('devices.add.header')}</DrawerHeader>
                <DrawerBody>
                    <Tabs size="md" h="full" lazyBehavior="unmount" isLazy isFitted>
                        <TabList mx="24px">
                            {!hasRedirect && (
                                <Tab isDisabled={isLoading}>{t('devices.add.number.tab')}</Tab>
                            )}
                            <Tab isDisabled={isLoading}>{t('devices.add.sipEndpoint.tab')}</Tab>
                            {isESimTabVisible && (
                                <Tab isDisabled={isLoading}>{t('devices.add.eSim.tab')}</Tab>
                            )}
                        </TabList>
                        <TabPanels h="calc(100% - 48px)">
                            {!hasRedirect && (
                                <TabPanel p={0} h="full">
                                    <AddDeviceNumberForm
                                        onClose={onClose}
                                        onSubmit={handleSubmit}
                                    />
                                </TabPanel>
                            )}
                            <TabPanel p={0} h="full">
                                <AddDeviceSipEndpointForm
                                    onClose={onClose}
                                    onSubmit={handleSubmit}
                                />
                            </TabPanel>
                            {isESimTabVisible && (
                                <TabPanel p={0} h="full">
                                    <AddDeviceESimForm onClose={onClose} onSubmit={handleSubmit} />
                                </TabPanel>
                            )}
                        </TabPanels>
                    </Tabs>
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    )
}
